import { FC, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'next-i18next';
import { Form, Radio } from 'antd';
import styled from 'styled-components';

import { ButtonIw } from '@/_pages/interwill/components/ButtonIw';
import { ButtonIwText } from '@/_pages/interwill/components/ButtonIwText';
import { FieldContainerIw } from '@/_pages/interwill/components/FieldContainerIw';
import {
  SelectBeneficiaryGroup,
  useApiBeneficiaries,
  useBeneficiariesActions,
  useBeneficiary,
} from '@/entities/beneficiary';
import { useBeneficiariesPidActions } from '@/entities/beneficiary/hooks/useBeneficiariesPidActions';
import { useIwEditingStore } from '@/entities/interwills';
import { getBeneficiaryFullName } from '@/entities/interwills/helpers';
import { useAddParticipantRole } from '@/entities/interwills/hooks/useAddParticipantRole';
import { AccountStatus } from '@/shared/api';
import { Input } from '@/shared/components';
import { useSJDatabase } from '@/shared/lib/sj-orm/hooks/use-sj-database';
import { DtoId } from '@/shared/lib/sj-orm/models/base.dto';
import { BeneficiaryGroup } from '@/shared/lib/sj-orm/models/beneficiary.dto';
import { DocumentType } from '@/shared/lib/sj-orm/models/document/document-base.dto';
import { Icon } from '@/shared/uikit/Icon';
import { objects } from '@/shared/utils/objects';
import { useModalsStore } from '@/widgets/modals/modals.store';
import { AddExecutorModalProps } from '@/widgets/modals/types';

import { ModalContainerV1 } from '../containers/ModalContainerV1';
import { InterwillStyledForm } from '../CreateEntityModal/styled';

type FormParams = {
  id: DtoId;
  nickName: string;
  firstName: string;
  lastName: string;
  secondName?: string;
  group: BeneficiaryGroup;
  email: string;
};

export const AddExecutorModal: FC<AddExecutorModalProps> = ({ isSubstitute }) => {
  const { t } = useTranslation('widgets/modals', { keyPrefix: 'addExecutor' });
  const { beneficiaries: apiBeneficiaries } = useApiBeneficiaries();

  const addRole = useAddParticipantRole();

  const [tab, setTab] = useState(0);

  const modals = useModalsStore();

  const { editedEntities, witnesses, executor, subExecutor, updateExecutor, updateSubExecutor } =
    useIwEditingStore();
  const sjDatabase = useSJDatabase();
  const { create } = useBeneficiariesActions();
  const { create: createPid } = useBeneficiariesPidActions();
  const { getBeneficiaryAdditionalInfo } = useBeneficiary();

  const beneficiaries = sjDatabase.collections.beneficiaries?.findMany(
    (item) =>
      item.id !== subExecutor?.id &&
      item.id !== executor?.id &&
      !witnesses.find((w) => w.id === item.id),
  );

  const {
    control,
    handleSubmit: formHandleSubmit,
    formState: { isSubmitting },
  } = useForm<FormParams>({
    defaultValues: {
      id: isSubstitute ? subExecutor?.id : executor?.id,
    },
  });

  const updateFn = isSubstitute ? updateSubExecutor : updateExecutor;

  const onSubmit = async (values: FormParams) => {
    let centralDbProfileId: number | undefined;
    if (tab === 0) {
      const benData = beneficiaries?.find((n) => n.id === values.id);

      if (benData) {
        const centralBeneficiaryData = apiBeneficiaries?.find(
          (item) => item.id === benData?.centralDbProfileId,
        );
        const isExist = centralBeneficiaryData?.status === AccountStatus.ACTIVE;
        centralDbProfileId = benData.centralDbProfileId;

        const additionalInfo = getBeneficiaryAdditionalInfo(benData.id);
        const obj = {
          id: values.id,
          firstName: benData.firstName,
          lastName: benData.lastName,
          secondName: benData.secondName,
          email: benData.email,
          hasAccount: isExist,
          birthDate: additionalInfo?.birthday,
          gender: additionalInfo?.gender,
          ...editedEntities[values.id],
        };
        updateFn(objects.copy(obj));
      }
    } else {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { id, ...filteredValues } = values;
      const newBen = await create({
        ...filteredValues,
        linkedCountryCode: '',
      });

      if (!newBen?.id) return;
      createPid({
        beneficiaryDtoId: newBen?.id,
        type: DocumentType.BENEFICIARY_PERSONAL_DATA,
        lastNameInNativeLanguage: '',
        nameInNativeLanguage: '',
      });
      centralDbProfileId = newBen.centralDbProfileId;

      updateFn({
        id: newBen?.id,
        firstName: values.firstName,
        lastName: values.lastName,
        secondName: values.secondName,
        email: values.email,
        hasAccount: false,
      });
    }
    addRole(centralDbProfileId, isSubstitute ? 'ALTERNATIVE_EXECUTOR' : 'EXECUTOR');

    modals.closeModal();
  };

  return (
    <ModalContainerV1 title={t(isSubstitute ? 'subTitle' : 'title')}>
      <InterwillStyledForm onFinish={formHandleSubmit(onSubmit)}>
        <InnerForm>
          {tab === 0 && (
            <>
              <b>{t(isSubstitute ? 'subSelectText' : 'selectText')}</b>
              <Controller
                name={'id'}
                control={control}
                rules={{ required: true }}
                render={({ field }) => (
                  <Radio.Group
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 12,
                    }}
                    options={beneficiaries?.map((item) => ({
                      label: getBeneficiaryFullName(item),
                      value: item.id,
                    }))}
                    {...field}
                  />
                )}
              />
              <ButtonIwText
                onClick={() => {
                  setTab(1);
                }}
                style={{ width: 'fit-content' }}
                icon={<Icon name="plus" size="s20" />}
              >
                {t(isSubstitute ? 'subAddNewBtn' : 'addNewBtn')}
              </ButtonIwText>
            </>
          )}
          {tab === 1 && (
            <>
              <Row>
                <Controller
                  name={'nickName'}
                  control={control}
                  rules={{ required: true }}
                  render={({ field, fieldState }) => {
                    return (
                      <FieldContainerIw label={t(`fields.${field.name}.label`)} required>
                        <Form.Item
                          validateStatus={fieldState.error ? 'error' : ''}
                          help={fieldState.error?.message}
                        >
                          <Input
                            placeholder={t(`fields.${field.name}.placeholder`)}
                            type={'text'}
                            {...field}
                          />
                        </Form.Item>
                      </FieldContainerIw>
                    );
                  }}
                />
                <Controller
                  name={'firstName'}
                  control={control}
                  rules={{ required: true }}
                  render={({ field, fieldState }) => {
                    return (
                      <FieldContainerIw label={t(`fields.${field.name}.label`)} required>
                        <Form.Item
                          validateStatus={fieldState.error ? 'error' : ''}
                          help={fieldState.error?.message}
                        >
                          <Input
                            placeholder={t(`fields.${field.name}.placeholder`)}
                            type={'text'}
                            {...field}
                          />
                        </Form.Item>
                      </FieldContainerIw>
                    );
                  }}
                />
              </Row>
              <Row>
                <Controller
                  name={'secondName'}
                  control={control}
                  render={({ field, fieldState }) => {
                    return (
                      <FieldContainerIw label={t(`fields.${field.name}.label`)}>
                        <Form.Item
                          validateStatus={fieldState.error ? 'error' : ''}
                          help={fieldState.error?.message}
                        >
                          <Input
                            placeholder={t(`fields.${field.name}.placeholder`)}
                            type={'text'}
                            {...field}
                          />
                        </Form.Item>
                      </FieldContainerIw>
                    );
                  }}
                />
                <Controller
                  name={'lastName'}
                  control={control}
                  rules={{ required: true }}
                  render={({ field, fieldState }) => {
                    return (
                      <FieldContainerIw label={t(`fields.${field.name}.label`)} required>
                        <Form.Item
                          validateStatus={fieldState.error ? 'error' : ''}
                          help={fieldState.error?.message}
                        >
                          <Input
                            placeholder={t(`fields.${field.name}.placeholder`)}
                            type={'text'}
                            {...field}
                          />
                        </Form.Item>
                      </FieldContainerIw>
                    );
                  }}
                />
              </Row>
              <Controller
                name={'group'}
                control={control}
                rules={{ required: true }}
                render={({ field, fieldState }) => {
                  return (
                    <FieldContainerIw label={t(`fields.${field.name}.label`)} required>
                      <Form.Item
                        validateStatus={fieldState.error ? 'error' : ''}
                        help={fieldState.error?.message}
                      >
                        <SelectBeneficiaryGroup
                          placeholder={t(`fields.${field.name}.placeholder`)}
                          {...field}
                        />
                      </Form.Item>
                    </FieldContainerIw>
                  );
                }}
              />
              <Controller
                name={'email'}
                control={control}
                rules={{ required: true }}
                render={({ field, fieldState }) => {
                  return (
                    <FieldContainerIw label={t(`fields.${field.name}.label`)} required>
                      <Form.Item
                        validateStatus={fieldState.error ? 'error' : ''}
                        help={fieldState.error?.message}
                      >
                        <Input
                          placeholder={t(`fields.${field.name}.placeholder`)}
                          type={'text'}
                          {...field}
                        />
                      </Form.Item>
                    </FieldContainerIw>
                  );
                }}
              />
            </>
          )}
        </InnerForm>
        <Buttons>
          <ButtonIw loading={isSubmitting} disabled={isSubmitting} htmlType="submit">
            {t('btn')}
          </ButtonIw>
        </Buttons>
      </InterwillStyledForm>
    </ModalContainerV1>
  );
};

const InnerForm = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  overflow: auto;
`;

const Buttons = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 40px;
`;

const Row = styled.div`
  display: flex;
  gap: 16px;
`;
